// ExamenModal integrado con LSP
// NOTA: dejé tu UI igual, solo añadí conexión LSP y manejo de diagnósticos.

import React, { useContext, useEffect, useState, useRef } from "react";
import { Button, message, Modal, Space, Tooltip, Tree, Typography } from "antd";
import { CloseOutlined, DeleteOutlined, EditOutlined, FileTextOutlined, ReadOutlined } from "@ant-design/icons";
import Editor from "@monaco-editor/react";
import { AuthContext } from "../../../../config/AuthContext";
import { useParams } from "react-router-dom";
import { getStompClient } from "../../../StompClient";
import TerminalJava from "./TerminalJava";
import { LuPackagePlus } from "react-icons/lu";
import { BsFiletypeJava } from "react-icons/bs";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {IoIosSend} from "react-icons/io";
import {PostJutgeProblemaIdUsuariId} from "../../../../dao/jutge/PostJutgeProblemaIdUsuariId";

const { Title } = Typography;

// --- TYPES ---
interface FileNode {
    key: string;
    name: string;
    type: "package" | "class";
    content?: string;
    children?: FileNode[];
    immutable?: boolean;
}

interface ExamenModalProps {
    visible: boolean;
    onClose: () => void;
    element: any;
}

// --- LSP HELPERS ---
// Construimos mensajes JSON-RPC estándar
const lspRequest = (id: number, method: string, params: any) => ({
    jsonrpc: "2.0",
    id,
    method,
    params
});

const lspNotify = (method: string, params: any) => ({
    jsonrpc: "2.0",
    method,
    params
});

// --- COMPONENTE ---
const ExamenModal: React.FC<ExamenModalProps> = ({ visible, onClose, element }) => {
    const { alumne, userId } = useContext(AuthContext);
    //const { id_activitat } = useParams();
    const editorRef = useRef<any>(null);
    const monacoRef = useRef<any>(null);
    const modelRef = useRef<any>(null);


    const [isRunning, setIsRunning] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [openIframe, setOpenIframe] = useState<"none" | "apuntes" | "enunciado">("none");

    // ------- LSP ---------
    const lspSocketRef = useRef<WebSocket | null>(null);
    const lspRequestId = useRef(1);

    const [diagnostics, setDiagnostics] = useState<Record<string, any[]>>({});

    // Convierte el nombre del examen en un nombre válido de clase Java
    const sanitizeClassName = (name: string): string => {
        if (!name) return "ClaseExamen";

        // quitar tildes
        const noAccents = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

        // reemplazar espacios por "_"
        let sanitized = noAccents.replace(/\s+/g, "_");

        // quitar caracteres no válidos
        sanitized = sanitized.replace(/[^a-zA-Z0-9_]/g, "");

        // si empieza con número, prefijar "_"
        if (/^[0-9]/.test(sanitized)) {
            sanitized = "_" + sanitized;
        }

        if (!sanitized) return "ClaseExamen";

        return sanitized;
    };

    const className = sanitizeClassName(element?.prac || "Examen");

    const initialTree: FileNode[] = [
        {
            key: "pkg-main",
            name: "main",
            type: "package",
            immutable: true,
            children: [
                {
                    key: `main.${className}.java`,
                    name: `${className}.java`,
                    type: "class",
                    content: element?.codi ? element.codi : `package main;
import java.util.*;
// Examen de: ${alumne?.alumne?.nom ?? ""} ${alumne?.alumne?.cognoms ?? ""}
public class ${className} {
    public static void main(String[] args) {
        // Escribe aquí tu código
    }
}`,
                    immutable: true,
                },
            ],
        },
    ];




    const [treeData, setTreeData] = useState<FileNode[]>(initialTree);
    const [selectedKey, setSelectedKey] = useState<string>("main.Examen.java");

    // -------- Helpers árbol --------
    const findNodeByKey = (nodes: FileNode[], key: string): FileNode | null => {
        for (let node of nodes) {
            if (node.key === key) return node;
            if (node.children) {
                const found = findNodeByKey(node.children, key);
                if (found) return found;
            }
        }
        return null;
    };

    const updateNodeContent = (nodes: FileNode[], key: string, content: string): FileNode[] =>
        nodes.map(node => {
            if (node.key === key && node.type === "class") return { ...node, content };
            if (node.children) return { ...node, children: updateNodeContent(node.children, key, content) };
            return node;
        });

    // --- LSP CONNECTION usando STOMP ---
    useEffect(() => {
        if (!visible) return;

        const client = getStompClient();

        const waitForConnection = () => {
            if (client.connected) {
                const subscription = client.subscribe(`/topic/lsp/${userId}`, (msg) => {
                    const data = JSON.parse(msg.body);

                    if (data.method === "textDocument/publishDiagnostics") {
                        const uri = data.params.uri.replace("file:///workspace/", "");
                        setDiagnostics(prev => ({
                            ...prev,
                            [uri]: data.params.diagnostics
                        }));
                    }
                });


                // Inicializar LSP con initialize + didOpen
                const files = buildFiles(treeData); // solo al montar
                client.publish({
                    destination: `/app/lsp/${userId}`,
                    body: JSON.stringify({
                        jsonrpc: "2.0",
                        id: Date.now(),
                        method: "initialize",
                        params: { rootUri: "file:///workspace", capabilities: {} }
                    })
                });

                Object.keys(files).forEach(path => {
                    client.publish({
                        destination: `/app/lsp/${userId}`,
                        body: JSON.stringify({
                            jsonrpc: "2.0",
                            method: "textDocument/didOpen",
                            params: { textDocument: { uri: `file:///workspace/${path}`, languageId: "java", version: 1, text: files[path] } }
                        })
                    });
                });
            } else {
                setTimeout(waitForConnection, 100);
            }
        };

        waitForConnection();

        return () => {
            if (client.connected) {
                client.unsubscribe(`/topic/lsp/${userId}`);
                client.publish({ destination: `/app/lsp/stop/${userId}`, body: "" });
            }
        };
    }, [visible, userId]); // <-- eliminar treeData

// --- Nuevo useEffect para analizar errores comunes en frontend ---
    /*
    useEffect(() => {
        if (!editorRef.current || !modelRef.current) return;

        const editor = editorRef.current;
        const model = modelRef.current;
        const value = model.getValue();

        const markers: any[] = [];

        // --- 1. Falta de ;
        const lines = value.split("\n");
        lines.forEach((line: any, i: number) => {
            const trimmed = line.trim();
            if (
                trimmed && // no vacía
                !trimmed.startsWith("//") && // no comentario
                !trimmed.endsWith(";") &&
                !trimmed.endsWith("{") &&
                !trimmed.endsWith("}") &&
                !trimmed.includes("class") &&
                !trimmed.includes("if") &&
                !trimmed.includes("for") &&
                !trimmed.includes("while") &&
                !trimmed.includes("else")
            ) {
                markers.push({
                    startLineNumber: i + 1,
                    startColumn: 1,
                    endLineNumber: i + 1,
                    endColumn: line.length + 1,
                    message: "Probable falta de ';'",
                    severity: monacoRef.current.MarkerSeverity.Error,
                });
            }
        });

        // --- 2. Métodos no existentes
        // Detectamos llamadas a métodos: foo(...);
        const methodCallRegex = /\b(\w+)\s*\(/g;
        let match: RegExpExecArray | null;
        while ((match = methodCallRegex.exec(value)) !== null) {
            const methodName = match[1];
            if (!value.includes(`void ${methodName}(`) && !value.includes(`int ${methodName}(`) && !value.includes(`String ${methodName}(`)) {
                const pos = model.getPositionAt(match.index);
                markers.push({
                    startLineNumber: pos.lineNumber,
                    startColumn: pos.column,
                    endLineNumber: pos.lineNumber,
                    endColumn: pos.column + methodName.length,
                    message: `Método '${methodName}' no definido`,
                    severity: monacoRef.current.MarkerSeverity.Error,
                });
            }
        }

        // --- 3. Variables no declaradas (básico)
        const varUsageRegex = /\b([a-zA-Z_]\w*)\b/g;
        const declaredVars = new Set<string>();
        const keywords = ["int","double","float","String","boolean","char","public","private","class","static","void","for","if","while","return"];
        lines.forEach((line: any) => {
            const declMatch = line.match(/\b(int|double|float|String|boolean|char)\s+([a-zA-Z_]\w*)/);
            if (declMatch) declaredVars.add(declMatch[2]);
        });
        let usageMatch: RegExpExecArray | null;
        while ((usageMatch = varUsageRegex.exec(value)) !== null) {
            const name = usageMatch[1];
            if (!declaredVars.has(name) && !keywords.includes(name) && !/System\.out/.test(name)) {
                const pos = model.getPositionAt(usageMatch.index);
                markers.push({
                    startLineNumber: pos.lineNumber,
                    startColumn: pos.column,
                    endLineNumber: pos.lineNumber,
                    endColumn: pos.column + name.length,
                    message: `Variable '${name}' no declarada`,
                    severity: monacoRef.current.MarkerSeverity.Warning,
                });
            }
        }

        // --- Aplicar markers
        monacoRef.current.editor.setModelMarkers(model, "frontend-lint", markers);

    }, [treeData]);
    */


// Envía cambios al LSP
    const notifyLspChange = (filePath: string, newText: string) => {
        if (!filePath) return;
        const client = getStompClient();
        const msg = {
            jsonrpc: "2.0",
            method: "textDocument/didChange",
            params: {
                textDocument: {
                    uri: `file:///workspace/${filePath}`,
                    version: Date.now()
                },
                contentChanges: [{ text: newText }]
            }
        };
        client.publish({
            destination: `/app/lsp/${userId}`,
            body: JSON.stringify(msg)
        });
    };

    const handleSave = async () => {
        const selectedNode = findNodeByKey(treeData, selectedKey);
        if (!selectedNode) return;

        const filename = selectedNode.name;
        const code = selectedNode.content || "";

        if (!filename.trim()) {
            message.warning("Has d’indicar un nom de fitxer.");
            return;
        }

        if (!filename.trim().toLowerCase().endsWith(".java")) {
            message.error("El nom del fitxer ha d'acabar amb .java");
            return;
        }

        setIsLoading(true);

        try {

            // 3️⃣ Guardar el código usando STOMP
            const client = getStompClient();
            client.publish({
                destination: `/app/guardar/${element.id || "0"}/usuario/${element.id_usuari}`,
                body: JSON.stringify({ [filename]: code })
            });

            // 1️⃣ Subir archivo al endpoint /upload
            const blob = new Blob([code], { type: "text/plain" });
            const formData = new FormData();
            formData.append("file", blob, filename);
            formData.append("uid", filename);

            const uploadResponse = await fetch(
                `${process.env.REACT_APP_API_URL}:8061/jutge/activitats/${element.id_practica || "0"}/problemes/${element.id}/usuaris/${element.id_usuari}/upload`,
                { method: "POST", body: formData }
            );

            if (!uploadResponse.ok) {
                message.error("Error al pujar el codi.");
                setIsLoading(false);
                return;
            }

            // 2️⃣ Enviar al juzgado
            const postResponse = await PostJutgeProblemaIdUsuariId(
                element.id + "",
                element.id_usuari + "",
                filename,
                element.id_practica || "0"
            );

        } catch (err) {
            console.error(err);
            message.error("Error durant l'enviament.");
        }

        setIsLoading(false);
    };



    // -------- Construcción paths --------
    const buildFiles = (nodes: FileNode[], currentPackage = ""): Record<string, string> => {
        let result: Record<string, string> = {};
        nodes.forEach(node => {
            if (node.type === "package") {
                const newPackage = currentPackage ? `${currentPackage}.${node.name}` : node.name;
                if (node.children) result = { ...result, ...buildFiles(node.children, newPackage) };
            } else if (node.type === "class" && node.content) {
                const packagePath = currentPackage.replace(/\./g, "/");
                const filePath = packagePath ? `${packagePath}/${node.name}` : node.name;
                result[filePath] = node.content;
            }
        });
        return result;
    };

    const selectedNode = findNodeByKey(treeData, selectedKey);
    const filePathSelected = (() => {
        if (!selectedNode || selectedNode.type !== "class") return "";

        const parts = selectedNode.key.split(".");
        const pkg = parts[0].replace("pkg-", "");
        const cls = selectedNode.name;
        return `${pkg}/${cls}`;
    })();

    // -------- Compilación por STOMP (tu backend actual) --------
    const handleCompileOrStop = () => {
        const client = getStompClient();
        setIsLoading(true);

        if (!isRunning) {
            window.dispatchEvent(new Event("clearTerminal"));

            const payload = buildFiles(treeData);

            client.publish({
                destination: `/app/compilar/${element.id}/usuario/${userId}`,
                body: JSON.stringify(payload),
            });

            setIsRunning(true);
            setIsLoading(false);

            // Si no llega evento de fin en X segundos, reset
            setTimeout(() => {
                if (isRunning) {
                    setIsRunning(false);
                    setIsLoading(false);
                    message.error("Tiempo de compilación agotado");
                }
            }, 30000);

        } else {
            client.publish({
                destination: `/app/stop/${element.id}/usuario/${userId}`,
                body: "",
            });
            setIsRunning(false);
            setIsLoading(false);
        }
    };


    useEffect(() => {
        const handler = () => {
            setIsRunning(false);
            setIsLoading(false);
        };
        window.addEventListener("compilationEnded", handler);
        return () => window.removeEventListener("compilationEnded", handler);
    }, []);

    const addPackage = () => {
        const pkgName = prompt("Nombre del nuevo package:", `package${Date.now()}`);
        if (!pkgName) return;
        setTreeData([...treeData, { key: `pkg-${pkgName}`, name: pkgName, type: "package", children: [] }]);
    };

    const addClass = () => {
        const parentNode = findNodeByKey(treeData, selectedKey);
        if (!parentNode || parentNode.type !== "package") {
            message.error("Selecciona un package para añadir la clase");
            return;
        }
        const className = prompt("Nombre de la nueva clase:", `Clase${Date.now()}`);
        if (!className) return;
        const newNode: FileNode = {
            key: `${parentNode.name}.${className}.java`,
            name: `${className}.java`,
            type: "class",
            content: `package ${parentNode.name};

public class ${className} {
    // Código de ${className}
}`,
        };
        parentNode.children = parentNode.children ? [...parentNode.children, newNode] : [newNode];
        setTreeData([...treeData]);
        setSelectedKey(newNode.key);
    };

// Mantén estas funciones tal cual estaban en tu código original:

    const deleteNodeByKey = (key: string) => {
        const node = findNodeByKey(treeData, key);
        if (!node) return;
        if (node.immutable) {
            message.error("Esta clase no se puede borrar");
            return;
        }
        const deleteRecursively = (nodes: FileNode[], key: string): FileNode[] =>
            nodes.filter(n => {
                if (n.key === key) return false;
                if (n.children) n.children = deleteRecursively(n.children, key);
                return true;
            });

        setTreeData(deleteRecursively(treeData, key));
        const firstNode = treeData[0]?.children?.[0]?.key || treeData[0]?.key || "";
        setSelectedKey(firstNode);
    };



    const renameNodeByKey = (key: string) => {
        const node = findNodeByKey(treeData, key);
        if (!node) return;
        const newName = prompt("Nuevo nombre:", node.name);
        if (!newName) return;

        const renameRecursively = (nodes: FileNode[]): FileNode[] =>
            nodes.map(n => {
                if (n.key === key) {
                    if (n.type === "class") {
                        return {
                            ...n,
                            name: newName,
                            key: `${n.key.split(".")[0]}.${newName}`,
                            content: `package ${n.key.split(".")[0]};

public class ${newName} {
${n.content?.split("\n").slice(3).join("\n")}
}`
                        };
                    } else {
                        return { ...n, name: newName, key: `pkg-${newName}` };
                    }
                }
                if (n.children) return { ...n, children: renameRecursively(n.children) };
                return n;
            });

        setTreeData(renameRecursively(treeData));
    };

    useEffect(() => {
        if (!editorRef.current || !monacoRef.current || !selectedNode) return;

        const monaco = monacoRef.current;

        // Crea o busca modelo
        let model = monaco.editor.getModel(monaco.Uri.parse(`file:///workspace/${filePathSelected}`));
        if (!model) {
            model = monaco.editor.createModel(selectedNode.content!, "java", monaco.Uri.parse(`file:///workspace/${filePathSelected}`));
        }

        modelRef.current = model;
        editorRef.current.setModel(model);

    }, [selectedNode, filePathSelected]);

    // --- Guardar/recuperar del localStorage ---
    const STORAGE_KEY = "examen_modal_tree";

    useEffect(() => {
        // Cargar desde localStorage
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setTreeData(parsed);
                setSelectedKey(parsed[0]?.children?.[0]?.key || parsed[0]?.key || "");
            } catch (e) {
                console.warn("Error cargando archivos guardados", e);
            }
        }
    }, []);

    useEffect(() => {
        // Guardar automáticamente cada vez que cambia treeData
        localStorage.setItem(STORAGE_KEY, JSON.stringify(treeData));
    }, [treeData]);


    useEffect(() => {
        if (!editorRef.current || !monacoRef.current || !modelRef.current) return;

        const editor = editorRef.current;
        const monaco = monacoRef.current;
        const model = modelRef.current;

        const markers = diagnostics[filePathSelected]?.map(d => ({
            startLineNumber: d.range.start.line + 1,
            startColumn: d.range.start.character + 1,
            endLineNumber: d.range.end.line + 1,
            endColumn: d.range.end.character + 1,
            message: d.message,
            severity: d.severity === 1
                ? monaco.MarkerSeverity.Error
                : monaco.MarkerSeverity.Warning
        })) || [];

        monaco.editor.setModelMarkers(model, "owner", markers);

    }, [diagnostics, filePathSelected]);



    // -------- Render --------
    return (
        <Modal
            open={visible}
            footer={null}
            closable={false}
            width="95vw"
            style={{ top: 20, padding: 0 }}
            bodyStyle={{ height: "90vh", display: "flex", flexDirection: "row", borderRadius: 8 }}
        >
            <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#1e1e1e", padding: 16 }}>

                {/* BARRA SUPERIOR */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <Space>
                        <Tooltip title="Abrir enunciado">
                            <Button icon={<FileTextOutlined />} onClick={() => setOpenIframe("enunciado")} />
                        </Tooltip>
                        {element?.apuntes && <Tooltip title="Abrir apuntes">
                            <Button icon={<ReadOutlined />} onClick={() => setOpenIframe("apuntes")} />
                        </Tooltip>}
                        {/*
                        <Button size="small" onClick={addPackage}><LuPackagePlus /></Button>
                        <Button size="small" onClick={addClass}><BsFiletypeJava /></Button>
                        */}
                    </Space>
                </div>

                <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

                    {/* ---------- TREE ---------- */}
                    <div style={{
                        width: 250,
                        marginRight: 16,
                        borderRight: "1px solid #444",
                        paddingRight: 8,
                        overflowY: "auto",
                        color: "#fff",
                        background: "#1e1e1e"
                    }}>
                        <Tree
                            treeData={treeData.map(transformNode)}
                            selectedKeys={[selectedKey]}
                            onSelect={(keys) => setSelectedKey(keys[0] as string)}
                            defaultExpandAll
                            showIcon
                            switcherIcon={<div style={{ color: "#fff" }}>▸</div>}
                            style={{ background: "#1e1e1e", color: "#fff" }}
                            titleRender={(node: any) => {
                                const currentNode = findNodeByKey(treeData, node.key);
                                const isSelected = currentNode?.key === selectedKey;
                                return (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            color: isSelected ? "#4ea1f3" : "#fff",
                                            fontWeight: isSelected ? "bold" : "normal",
                                        }}
                                    >
                                        <span>{currentNode?.name}</span>
                                        <span>

                                    {!currentNode?.immutable && (
                                        <>
                                            <EditOutlined
                                                style={{ color: "#4ea1f3", marginRight: 8 }}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    renameNodeByKey(currentNode!.key);
                                                }}
                                            />
                                            <DeleteOutlined
                                                style={{ color: "#f56565" }}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    deleteNodeByKey(currentNode!.key);
                                                }}
                                            />
                                        </>
                                    )}
                                    </span>
                            </div>
                            );
                        }}
                    />

                    </div>

                    {/* ---------- EDITOR + TERMINAL ---------- */}
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <Title level={4} style={{ color: "#fff", marginBottom: 8 }}>Editor Java</Title>

                        {selectedNode && selectedNode.type === "class" && (
                            <Editor
                                height="70%"
                                defaultLanguage="java"
                                theme="vs-dark"
                                value={selectedNode.content}
                                onChange={val => {
                                    const newText = val || "";
                                    setTreeData(updateNodeContent(treeData, selectedNode.key, newText));
                                    // NO enviamos al LSP aquí
                                }}
                                onMount={(editor, monaco) => {
                                    editorRef.current = editor;
                                    monacoRef.current = monaco;
                                    modelRef.current = editor.getModel();
                                    const applyDiagnostics = () => {
                                        monaco.editor.setModelMarkers(
                                            editor.getModel()!,
                                            "owner",
                                            diagnostics[filePathSelected]?.map(d => ({
                                                startLineNumber: d.range.start.line + 1,
                                                startColumn: d.range.start.character + 1,
                                                endLineNumber: d.range.end.line + 1,
                                                endColumn: d.range.end.character + 1,
                                                message: d.message,
                                                severity: d.severity === 1 ? monaco.MarkerSeverity.Error : monaco.MarkerSeverity.Warning
                                            })) || []
                                        );
                                    };

                                    applyDiagnostics();
                                }}

                            />

                        )}

                        <div style={{ display: "flex", marginTop: 8, justifyContent: "space-between" }}>
                            <Space>
                                <Button type="primary" danger={isRunning} loading={isLoading} onClick={handleCompileOrStop}>
                                    {isRunning ? "Parar" : "Compilar"}
                                </Button>
                                <Button onClick={() => window.dispatchEvent(new Event("clearTerminal"))}>Limpiar consola</Button>
                            </Space>

                            <Space>
                                <Button
                                    type="default"
                                    icon={<span style={{ fontSize: 16, color: "green" }}><IoIosSend /></span>}
                                    onClick={() => {
                                        handleSave();
                                    }}
                                />
                                <Button
                                    type="default"
                                    icon={<CloseOutlined />}
                                    onClick={onClose}
                                />
                            </Space>
                        </div>

                        <div style={{ flex: 1, marginTop: 8, overflowY: "auto" }}>
                            <TerminalJava
                                activityId={element.id}
                                userId={userId}
                                setIsRunning={setIsRunning}
                                setIsLoading={setIsLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ---------- MODAL DE ENUNCIADO (MARKDOWN) ---------- */}
            {openIframe === "enunciado" && (
                <div style={{
                    position: "absolute",
                    top: 50,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "80%",
                    height: "80%",
                    backgroundColor: "#fff",
                    zIndex: 999,
                    borderRadius: 8,
                    overflow: "auto",
                    padding: 24,
                    boxShadow: "0 0 20px rgba(0,0,0,0.5)"
                }}>
                    <Button
                        icon={<CloseOutlined />}
                        size="small"
                        style={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}
                        onClick={() => setOpenIframe("none")}
                    />

                    <h2>Enunciado</h2>

                    <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}
                                       rehypePlugins={[rehypeRaw]}
                                       skipHtml={false}
                                       unwrapDisallowed
                        >
                            {element.enunciat || "*No hay enunciado disponible.*"}
                        </ReactMarkdown>
                    </div>
                    <h2>E/S</h2>

                    <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                        <h6>Entrada:</h6>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}
                                       rehypePlugins={[rehypeRaw]}
                                       skipHtml={false}
                                       unwrapDisallowed
                        >
                            {element.entrada || "*No hay entrada.*"}
                        </ReactMarkdown>
                    </div>
                    <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                        <h6>Salida:</h6>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}
                                       rehypePlugins={[rehypeRaw]}
                                       skipHtml={false}
                                       unwrapDisallowed
                        >
                            {element.sortida || "*No hay salida.*"}
                        </ReactMarkdown>
                    </div>
                </div>
            )}


            {/* ---------- IFRAME FLOTANTE ---------- */}
            {openIframe === "apuntes" && (
                <div style={{
                    position: "absolute",
                    top: 50,
                    left: 50,
                    width: "80%",
                    height: "80%",
                    backgroundColor: "#fff",
                    zIndex: 999,
                    borderRadius: 8,
                    overflow: "hidden",
                    boxShadow: "0 0 20px rgba(0,0,0,0.5)"
                }}>
                    <Button
                        icon={<CloseOutlined />}
                        size="small"
                        style={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}
                        onClick={() => setOpenIframe("none")}
                    />
                    <iframe
                        src={"https://daw-0485-prog-5e77eb.gitlab.io"}
                        style={{ width: "100%", height: "100%", border: "none" }}
                    />
                </div>
            )}
        </Modal>
    );
};

const transformNode = (node: FileNode): any => ({
    title: node.name,
    key: node.key,
    children: node.children?.map(transformNode),
    isLeaf: node.type === "class",
});

export default ExamenModal;